import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { LexicalNestedComposer } from '@lexical/react/LexicalNestedComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
    $getRoot,
    $createParagraphNode,
    $createTextNode,
    $getSelection,
    $isRangeSelection,
    createEditor,
    KEY_DOWN_COMMAND,
    PASTE_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
} from 'lexical';

import { MathSymbols } from '@/src/lib';
import SingleLinePlugin from '../plugins/SingleLinePlugin';
import { BlockSlotEnterPlugin } from '../plugins/BlockEnterPlugin';
import { AllowedKeysContext } from '../components/AllowedKeysContext';
import {
    FractionNode,
    ExponentNode,
    RootNode,
    OverlineNode,
    $createFractionNode,
    $createExponentNode,
    $createRootNode,
    $createOverlineNode,
} from '../components/MathNodes';
import type { SerializedSubstate } from '../../utils/mathTypes';

type SlotEditorPluginProps = {
    substate: SerializedSubstate;
    onChange: (newState: string) => void;
    className?: string;
    depth?: number; // Current nesting depth (0 = main editor)
    maxDepth?: number; // Maximum allowed depth (default 3)
    onTabNext?: () => void;
    onTabPrev?: () => void;
    onEscapeLeft?: () => void;
    onEscapeRight?: () => void;
};

export default function SlotEditorPlugin({
    substate,
    onChange,
    className,
    depth = 0,
    maxDepth = 2,
    onTabNext,
    onTabPrev,
    onEscapeLeft,
    onEscapeRight,
}: SlotEditorPluginProps) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const nestedEditor = useMemo(
        () =>
            createEditor({
                namespace: 'SlotEditor',
                nodes: [FractionNode, ExponentNode, RootNode, OverlineNode],
                onError: (error) => {
                    console.error('Slot editor error:', error);
                },
            }),
        []
    );
    const [isEmpty, setIsEmpty] = useState(true);
    const lastHydratedStateRef = useRef<string>('');
    const isLocalChangeRef = useRef<boolean>(false);

    const allowedKeysCtx = useContext(AllowedKeysContext);
    const allowedSet = useMemo(() => new Set(allowedKeysCtx ?? []), [allowedKeysCtx]);

    const printableAllowed = useMemo(() => {
        if (allowedSet.size === 0) return null; // no filtering
        const charMap = new Set<string>();

        const allSymbols: [string, string][] = [
            ...Object.entries(MathSymbols.numbers),
            ...Object.entries(MathSymbols.letters_lower),
            ...Object.entries(MathSymbols.letters_upper),
            ...Object.entries(MathSymbols.symbols),
        ];
        for (const [key, value] of allSymbols) {
            if (allowedSet.has(key)) {
                charMap.add(value);
            }
        }
        return charMap;
    }, [allowedSet]);
    const isFreeEnabled = useMemo(() => allowedSet.has('free'), [allowedSet]);

    const isEditingKey = (e: KeyboardEvent) => {
        // Always permit navigation/editing shortcuts
        const k = e.key;
        if (e.ctrlKey || e.metaKey || e.altKey) return true; // copy/paste/select-all/undo/redo, etc.
        // Navigation + editing keys
        return (
            k === 'Backspace' ||
            k === 'Delete' ||
            k === 'ArrowLeft' ||
            k === 'ArrowRight' ||
            k === 'ArrowUp' ||
            k === 'ArrowDown' ||
            k === 'Home' ||
            k === 'End' ||
            k === 'Tab' ||
            k === 'Escape'
        );
    };

    // Hydrate when substate.state changes (but skip if the change came from within this editor)
    useEffect(() => {
        nestedEditor.setEditable(true);

        const currentState = substate?.state || '';

        // Skip if this is a local change (originated from this editor's onChange)
        if (isLocalChangeRef.current) {
            isLocalChangeRef.current = false;
            return;
        }

        if (currentState === lastHydratedStateRef.current) {
            return;
        }

        lastHydratedStateRef.current = currentState;

        if (!currentState) {
            nestedEditor.update(() => {
                $getRoot().clear();
            });
            setIsEmpty(true);
            return;
        }

        try {
            const parsed = JSON.parse(currentState);

            nestedEditor.update(() => {
                const root = $getRoot();
                root.clear();

                if (parsed.root && parsed.root.children) {
                    parsed.root.children.forEach((childData: any) => {
                        if (childData.type === 'paragraph' && childData.children) {
                            const para = $createParagraphNode();
                            childData.children.forEach((nodeData: any) => {
                                if (nodeData.type === 'text') {
                                    const textNode = $createTextNode(nodeData.text);
                                    // Preserve text formatting
                                    if (nodeData.format) textNode.setFormat(nodeData.format);
                                    if (nodeData.style) textNode.setStyle(nodeData.style);
                                    para.append(textNode);
                                } else if (nodeData.type === 'fraction') {
                                    const fracNode = FractionNode.importJSON(nodeData);
                                    para.append(fracNode);
                                } else if (nodeData.type === 'exponent') {
                                    const expNode = ExponentNode.importJSON(nodeData);
                                    para.append(expNode);
                                } else if (nodeData.type === 'root') {
                                    const rootNode = RootNode.importJSON(nodeData);
                                    para.append(rootNode);
                                } else if (nodeData.type === 'overline') {
                                    const overlineNode = OverlineNode.importJSON(nodeData);
                                    para.append(overlineNode);
                                }
                            });
                            root.append(para);
                        }
                    });
                }
            });

            setTimeout(() => {
                nestedEditor.getEditorState().read(() => {
                    const text = $getRoot().getTextContent().trim();
                    setIsEmpty(text.length === 0);
                });
            }, 0);
        } catch (e) {
            console.error('Error hydrating slot:', e);
            nestedEditor.update(() => {
                $getRoot().clear();
            });
            setIsEmpty(true);
        }
    }, [nestedEditor, substate?.state]);

    // Hard key filtering for this SlotEditor
    useEffect(() => {
        if (!printableAllowed) return;

        const unsubKeyDown = nestedEditor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (!event) return false;
                if (isFreeEnabled) return false;

                // Always allow editing/navigation keys
                if (isEditingKey(event)) return false;

                const key = event.key;

                // Special case: convert minus/hyphen to en-dash if 'sub' is allowed
                if (key === '-' && allowedSet.has('sub')) {
                    event.preventDefault();
                    nestedEditor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            selection.insertText('–'); // en-dash
                        }
                    });
                    return true;
                }
                if (key === ' ' && allowedSet.has('spc')) {
                    return false;
                }

                // Check if key is in allowed set
                if (key && printableAllowed.has(key)) {
                    return false; // allowed -> let Lexical handle it
                }

                // Block everything else
                event.preventDefault();
                event.stopPropagation();
                return true;
            },
            COMMAND_PRIORITY_CRITICAL
        );

        // Sanitize paste to only allowed characters
        const unsubPaste = nestedEditor.registerCommand(
            PASTE_COMMAND,
            (event: ClipboardEvent) => {
                if (!event || !event.clipboardData) return false;
                const raw = event.clipboardData.getData('text/plain') ?? '';
                const cleaned = raw
                    .split('')
                    .filter((ch) => printableAllowed.has(ch))
                    .join('');
                event.preventDefault();
                if (cleaned.length > 0) {
                    nestedEditor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            selection.insertText(cleaned);
                        }
                    });
                }
                return true;
            },
            COMMAND_PRIORITY_CRITICAL
        );

        return () => {
            unsubKeyDown();
            unsubPaste();
        };
    }, [nestedEditor, printableAllowed, allowedSet, isFreeEnabled]);

    // Listen for custom node insertion events from the keypad
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const handleNodeInsert = (e: Event) => {
            const customEvent = e as CustomEvent<{
                kind: 'frac' | 'sqrt' | 'nrt' | 'exp' | 'segment' | 'line' | 'ray';
            }>;
            const { kind } = customEvent.detail;

            // Check if we're at max depth
            if (depth >= maxDepth) {
                return;
            }

            nestedEditor.update(() => {
                const sel = $getSelection();
                if (!$isRangeSelection(sel)) return;

                let node: any;
                switch (kind) {
                    case 'frac':
                        node = $createFractionNode();
                        sel.insertNodes([node]);
                        break;
                    case 'exp':
                        node = $createExponentNode();
                        sel.insertNodes([node]);
                        break;
                    case 'sqrt':
                        node = $createRootNode(false);
                        node.__index = null;
                        sel.insertNodes([node]);
                        break;
                    case 'nrt':
                        node = $createRootNode(true);
                        node.__index = { state: '' };
                        sel.insertNodes([node]);
                        break;
                    case 'segment':
                    case 'line':
                    case 'ray':
                        node = $createOverlineNode(kind);
                        sel.insertNodes([node]);
                        break;
                }

                // Focus the first slot of the newly inserted node
                if (node) {
                    const nodeKey = node.getKey();
                    setTimeout(() => {
                        const dom = nestedEditor.getElementByKey(nodeKey);
                        if (dom) {
                            const firstSlot = dom.querySelector<HTMLElement>(
                                kind === 'nrt'
                                    ? '.pm-math-index [contenteditable="true"]'
                                    : '.pm-math-num [contenteditable="true"], ' +
                                          '.pm-math-base [contenteditable="true"], ' +
                                          '.pm-math-radicand [contenteditable="true"], ' +
                                          '.pm-math-overline [contenteditable="true"]'
                            );
                            firstSlot?.focus();
                        }
                    }, 0);
                }
            });
        };

        wrapper.addEventListener('math-node-insert', handleNodeInsert);
        return () => {
            wrapper.removeEventListener('math-node-insert', handleNodeInsert);
        };
    }, [nestedEditor, depth, maxDepth]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onTabNext) {
                onTabNext();
            } else {
                onEscapeRight?.();
            }
            return;
        }
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onTabPrev) {
                onTabPrev();
            } else {
                onEscapeLeft?.();
            }
            return;
        }

        if (e.key === 'ArrowLeft') {
            const sel = getSelection();
            if (sel && sel.anchorOffset === 0 && sel.focusOffset === 0) {
                if (onEscapeLeft) {
                    onEscapeLeft();
                    e.preventDefault();
                }
            }
            return;
        }
        if (e.key === 'ArrowRight') {
            const el = wrapperRef.current?.querySelector(
                '[contenteditable="true"]'
            ) as HTMLElement | null;
            if (el != null) {
                const range = document.getSelection()?.getRangeAt(0);
                if (range != null && el.textContent != null) {
                    const atEnd = range.endOffset === el.textContent.length;
                    if (atEnd && onEscapeRight) {
                        onEscapeRight();
                        e.preventDefault();
                    }
                }
            }
        }
    };

    return (
        <LexicalNestedComposer initialEditor={nestedEditor}>
            <div
                ref={wrapperRef}
                className={`pm-math-slot ${className ?? ''}`}
                data-empty={isEmpty ? 'true' : 'false'}
                onKeyDown={handleKeyDown}
            >
                <RichTextPlugin
                    contentEditable={<ContentEditable spellCheck={false} />}
                    placeholder={null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <SingleLinePlugin />
                <OnChangePlugin
                    onChange={(editorState) => {
                        let empty = true;
                        editorState.read(() => {
                            empty = $getRoot().getTextContent().trim().length === 0;
                        });
                        setIsEmpty(empty);
                        const json = JSON.stringify(editorState.toJSON());

                        // Mark that this change originated from within this editor
                        isLocalChangeRef.current = true;
                        onChange(json);
                    }}
                />
            </div>
            <BlockSlotEnterPlugin
                onEnter={() => {
                    if (onTabNext) {
                        onTabNext();
                    } else {
                        onEscapeRight?.();
                    }
                }}
                onShiftEnter={() => {
                    if (onTabPrev) {
                        onTabPrev();
                    } else {
                        onEscapeLeft?.();
                    }
                }}
            />
        </LexicalNestedComposer>
    );
}
